/**
 * Intranet Osasco Plaza — src/modules/gis.jsx
 * Telas do módulo GIS (vendas diárias e auditoria). Extraído de app.jsx —
 * ver DOC/ADR/0011.
 */

import { Blocked, Icon, buildCalendar, fmtBRL, getDayKey, hasPerm, parseBRL } from './shared.jsx';
const { useState, useEffect, useRef } = React;


/* ════════════════════════════════════════════════════════════════
   MÓDULO GIS — ADMINISTRAÇÃO (Desbloqueio de Períodos)
   ════════════════════════════════════════════════════════════════ */
export function GISAdminScreen({ user, onChange }) {
  if (!hasPerm(user, 'gis', 'approve')) return <Blocked/>;

  const today   = new Date();
  const [viewYear,  setViewYear]  = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  /* O seletor agora lista LOJAS, vindas do servidor.

     Antes lia `DB.users` do localStorage — que ficou vazio quando os usuários
     migraram para o D1 na Fundação, deixando esta tela sem nenhuma opção no
     combo. Regressão silenciosa: a tela abria normalmente, só não tinha o que
     escolher.

     Listar loja em vez de usuário também corrige o conceito: o faturamento é
     da loja, e uma loja pode ter várias pessoas lançando. */
  const [storeUsers, setStoreUsers] = useState([]);   // agora são lojas: { id, nome }
  const [selectedUserId, setSelectedUserId] = useState('');
  const [diasLiberados, setDiasLiberados] = useState({});

  useEffect(() => {
    let vivo = true;
    API.gis.lojas()
      .then(d => {
        if (!vivo) return;
        const lojas = (d.lojas || []).map(l => ({ id: l.id, nome: l.nome, store: l.nome }));
        setStoreUsers(lojas);
        setSelectedUserId(prev => prev || lojas[0]?.id || '');
      })
      .catch(e => setFeedback({ msg: e.message || 'Não foi possível carregar as lojas.', tone: 'err' }));
    return () => { vivo = false; };
  }, []);

  /* Load encrypted GIS record for selected user */
  const [vendas,    setVendas]    = useState({});
  const [sentWeeks, setSentWeeks] = useState({});
  const [loading,   setLoading]   = useState(false);
  const [feedback,  setFeedback]  = useState(null); // { msg, tone }
  const [confirm,   setConfirm]   = useState(null); // { type, label, payload }

  const MONTH_NAMES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
  const DAY_NAMES   = ['DOM','SEG','TER','QUA','QUI','SEX','SÁB'];

  /* ── Load & decrypt selected user's GIS data ── */
  const loadUserData = async (uid) => {
    setLoading(true); setFeedback(null);
    setVendas({}); setSentWeeks({}); setDiasLiberados({});
    try {
      const anoMes = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`;
      const d = await API.gis.mes(anoMes, uid);
      const porDia = {};
      for (const [data, info] of Object.entries(d.dias || {})) {
        porDia[data] = { value: info.valor, por: info.por, em: info.em };
      }
      setVendas(porDia);
      setSentWeeks(d.semanasFechadas || {});
      setDiasLiberados(d.diasLiberados || {});
    } catch(e) {
      setFeedback({ msg: e.message || 'Não foi possível carregar os lançamentos.', tone:'err' });
    }
    setLoading(false);
  };

  useEffect(() => { if (selectedUserId) loadUserData(selectedUserId); }, [selectedUserId, viewYear, viewMonth]);

  /* ── Desbloqueio ──────────────────────────────────────────────
     Antes toda a lógica morava aqui: o navegador desmontava a chave
     `gis_ANO_MES_wN`, remontava o calendário para descobrir a que semana o dia
     pertencia, e reescrevia o mapa cifrado. Agora é uma chamada por ação, e
     quem decide o que pode ser desbloqueado é o servidor.

     Liberar um DIA é diferente de reabrir a SEMANA: reabrir devolve ao
     lojista a chance de mexer em tudo, inclusive no que já estava conferido.
     Quando só um dia está errado, liberar só ele preserva o resto da
     declaração. */
  const anoMesAdmin = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`;

  const unlockDay = (dayKey) => {
    setConfirm({ type:'day', label: `Dia ${dayKey}`, payload: dayKey });
  };
  const unlockWeek = (weekIdx) => {
    setConfirm({ type:'week', label: `Semana ${weekIdx + 1} de ${MONTH_NAMES[viewMonth]}/${viewYear}`, payload: weekIdx });
  };
  const unlockMonth = () => {
    setConfirm({ type:'month', label: `Mês inteiro de ${MONTH_NAMES[viewMonth]}/${viewYear}`, payload: null });
  };

  const executeUnlock = async () => {
    if (!confirm) return;
    try {
      if (confirm.type === 'day') {
        await API.gis.liberarDia(confirm.payload, selectedUserId);
      } else if (confirm.type === 'week') {
        await API.gis.reabrirSemana(anoMesAdmin, confirm.payload, selectedUserId);
      } else if (confirm.type === 'month') {
        /* Uma chamada por semana fechada. Sem endpoint "reabrir o mês" de
           propósito: reabrir um mês inteiro é ação rara e grave, e vale
           aparecer na auditoria como as N reaberturas que de fato são. */
        const fechadas = Object.keys(sentWeeks).map(Number).sort((a, b) => a - b);
        for (const wi of fechadas) await API.gis.reabrirSemana(anoMesAdmin, wi, selectedUserId);
      }
      await loadUserData(selectedUserId);
      setFeedback({ msg:`✅ ${confirm.label} desbloqueado com sucesso.`, tone:'ok' });
      setTimeout(() => setFeedback(null), 4000);
    } catch(e) {
      setFeedback({ msg: e.message || 'Não foi possível desbloquear.', tone:'err' });
    }
    setConfirm(null);
  };

  /* ── Calendar helpers ── */
  const weeks = buildCalendar(viewYear, viewMonth);
  const isSentWeek = (wi) => !!sentWeeks[wi];
  const isDayUnlocked = (dayKey) => !!diasLiberados[dayKey];
  const monthHasSent = weeks.some((_, wi) => isSentWeek(wi));
  const getDayVal = (dayKey) => {
    const v = vendas[dayKey];
    return (v && v.value !== null && v.value !== undefined) ? v.value : null;
  };

  /* `selectedUser` virou a LOJA selecionada — o nome da variável ficou por
     causa dos ~15 usos abaixo, mas o conteúdo é `{ id, nome, store }`. */
  const selectedUser = storeUsers.find(u => u.id === selectedUserId);

  return (
    <div className="gis-shell">

      {/* Confirm modal */}
      {confirm && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth:440 }}>
            <button className="close-x" onClick={() => setConfirm(null)}><Icon name="x" size={16}/></button>
            <div style={{ fontSize:32, textAlign:'center', marginBottom:12 }}>🔓</div>
            <h3 style={{ textAlign:'center' }}>Confirmar Desbloqueio</h3>
            <p style={{ fontSize:13, color:'var(--ink-2)', margin:'12px 0 20px', textAlign:'center', lineHeight:1.55 }}>
              Deseja reabilitar o envio de <strong>{confirm.label}</strong> para a loja <strong>{selectedUser?.store || selectedUser?.nome}</strong>?<br/>
              <span style={{ fontSize:12, color:'var(--ink-3)' }}>O lojista poderá editar e reenviar o período.</span>
            </p>
            <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
              <button className="btn btn-ghost" onClick={() => setConfirm(null)}>Cancelar</button>
              <button className="btn btn-gold" onClick={executeUnlock}><Icon name="unlock" size={14}/> Desbloquear</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="gis-topbar">
        <div className="page-title">
          <h2 style={{ display:'flex', alignItems:'center', gap:10 }}>
            <Icon name="key" size={22} style={{ color:'var(--navy)' }}/>
            Auditoria - Gestão de Períodos
          </h2>
          <p>Reabilite dias, semanas ou meses enviados por lojistas</p>
        </div>
        <div className="gis-nav-month">
          <span className="gis-month-adj" onClick={() => { if(viewMonth===0){setViewYear(y=>y-1);setViewMonth(11);}else setViewMonth(m=>m-1); }}>
            ‹ {MONTH_NAMES[viewMonth===0?11:viewMonth-1].slice(0,3)}
          </span>
          <div className="gis-month-label">{MONTH_NAMES[viewMonth]} / {viewYear}</div>
          <span className="gis-month-adj" onClick={() => { if(viewMonth===11){setViewYear(y=>y+1);setViewMonth(0);}else setViewMonth(m=>m+1); }}>
            {MONTH_NAMES[viewMonth===11?0:viewMonth+1].slice(0,3)} ›
          </span>
        </div>
      </div>

      {/* Loja selector */}
      <div className="card" style={{ marginBottom:20 }}>
        <div style={{ display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, flex:1, minWidth:260 }}>
            <Icon name="building" size={16} style={{ color:'var(--navy)', flexShrink:0 }}/>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:11, fontWeight:600, color:'var(--ink-3)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4 }}>Selecionar Loja / Responsável</div>
              <select
                value={selectedUserId}
                onChange={e => setSelectedUserId(e.target.value)}
                style={{ width:'100%', height:38, background:'var(--surface-2)', border:'1px solid var(--line)', borderRadius:8, padding:'0 12px', fontSize:13, color:'var(--ink)', outline:'none', fontFamily:'inherit' }}
              >
                {storeUsers.length === 0 && <option value="">Nenhum lojista cadastrado</option>}
                {storeUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.nome}</option>
                ))}
              </select>
            </div>
          </div>
          {/* Os cards de Cargo/Perfil saíram: eles descreviam o USUÁRIO
              selecionado, e o alvo desta tela passou a ser a LOJA. Mostrar o
              cargo de alguém aqui sugeriria que o lançamento é daquela pessoa,
              quando é da loja inteira. */}
          {monthHasSent && (
            <button className="btn btn-danger" onClick={unlockMonth} title="Desbloquear todo o mês">
              <Icon name="unlock" size={14}/> Desbloquear Mês Inteiro
            </button>
          )}
        </div>
      </div>

      {/* Feedback banner */}
      {feedback && (
        <div style={{ padding:'10px 16px', borderRadius:8, fontSize:13, marginBottom:16,
          background: feedback.tone==='ok' ? 'var(--ok-bg)' : 'var(--err-bg)',
          color:      feedback.tone==='ok' ? 'var(--ok)'    : 'var(--err)',
          border: `1px solid ${feedback.tone==='ok' ? 'var(--ok)' : 'var(--err)'}`,
          display:'flex', alignItems:'center', gap:8 }}>
          <Icon name={feedback.tone==='ok'?'check-circle':'x'} size={15}/>
          {feedback.msg}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign:'center', padding:60, color:'var(--ink-3)' }}>
          <Icon name="shield" size={28} style={{ display:'block', margin:'0 auto 12px', color:'var(--navy)' }}/>
          <div style={{ fontSize:13 }}>Descriptografando dados da loja…</div>
        </div>
      ) : storeUsers.length === 0 ? (
        <div className="card" style={{ textAlign:'center', padding:48 }}>
          <Icon name="building" size={32} style={{ display:'block', margin:'0 auto 12px', color:'var(--ink-4)' }}/>
          <div style={{ fontWeight:600, color:'var(--ink-2)', marginBottom:6 }}>Nenhum lojista cadastrado</div>
          <p style={{ fontSize:13, color:'var(--ink-3)' }}>Cadastre usuários com o campo "Loja" preenchido no módulo Usuários.</p>
        </div>
      ) : (
        <>
          {/* Calendar */}
          <div className="gis-calendar">
            {/* Header */}
            <div className="gis-cal-head">
              {DAY_NAMES.map(d => (
                <div key={d} className="gis-cal-head-cell">{d}</div>
              ))}
              <div className="gis-cal-head-cell total-col">Semana</div>
            </div>

            {weeks.map((week, wi) => {
              const sent = isSentWeek(wi);
              const wTotal = (() => {
                let t = 0; let any = false;
                week.forEach(c => {
                  if (!c.outside && c.month === viewMonth) {
                    const v = getDayVal(getDayKey(viewYear, viewMonth, c.day));
                    if (v !== null) { t += v; any = true; }
                  }
                });
                return any ? t : null;
              })();

              return (
                <div key={wi} className="gis-week-row">
                  {week.map((cell, di) => {
                    const outside = cell.outside;
                    const isWE = di === 0 || di === 6;
                    const dayKey = !outside ? getDayKey(viewYear, cell.month, cell.day) : null;
                    const val = dayKey ? getDayVal(dayKey) : null;
                    const dayUnlocked = dayKey ? isDayUnlocked(dayKey) : false;
                    const todayCell = !outside && new Date(viewYear, viewMonth, cell.day).toDateString() === today.toDateString();

                    let cellClass = 'gis-day-cell';
                    if (isWE)     cellClass += ' weekend';
                    if (outside)  cellClass += ' outside';
                    if (todayCell) cellClass += ' today';
                    if (val !== null) cellClass += ' has-value';

                    return (
                      <div key={di} className={cellClass} style={{ position:'relative' }}>
                        {!outside && (
                          <>
                            <div className="gis-day-num" style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                              <span>{cell.day}</span>
                              {sent && val !== null && !dayUnlocked && cell.month === viewMonth && (
                                <button
                                  title="Desbloquear este dia"
                                  onClick={() => unlockDay(dayKey)}
                                  style={{ background:'transparent', border:'none', cursor:'pointer', padding:'1px 3px', borderRadius:4, color:'var(--warn)', lineHeight:1 }}
                                >
                                  <Icon name="unlock" size={11}/>
                                </button>
                              )}
                              {dayUnlocked && (
                                <span title="Dia desbloqueado" style={{ color:'var(--ok)', fontSize:10 }}>🔓</span>
                              )}
                            </div>
                            {val !== null && cell.month === viewMonth && (
                              <div style={{
                                marginTop:'auto', fontSize:11, fontFamily:'JetBrains Mono,monospace', fontWeight:600,
                                textAlign:'right', padding:'3px 4px', borderRadius:5,
                                background: sent && !dayUnlocked ? 'var(--warn-bg)' : 'var(--ok-bg)',
                                color:      sent && !dayUnlocked ? 'var(--warn)'    : 'var(--ok)',
                                border:    `1px solid ${sent && !dayUnlocked ? '#fcd34d' : 'rgba(22,163,74,0.2)'}`,
                              }}>
                                {fmtBRL(val)}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}

                  {/* Total + unlock week */}
                  <div className="gis-day-cell gis-total-cell">
                    <div className="total-label">Semana {wi+1}</div>
                    {wTotal !== null ? (
                      <div className="total-value has-value">{fmtBRL(wTotal)}</div>
                    ) : (
                      <div className="total-value empty">—</div>
                    )}
                    {sent ? (
                      <div style={{ marginTop:6, display:'flex', flexDirection:'column', gap:4 }}>
                        <span className="gis-week-sent-badge"><Icon name="check" size={10}/> Enviada</span>
                        <button className="btn btn-sm" style={{ fontSize:10, height:22, padding:'0 8px', background:'var(--warn-bg)', border:'1px solid var(--warn)', color:'var(--warn)' }}
                          onClick={() => unlockWeek(wi)}>
                          <Icon name="unlock" size={11}/> Desbloquear
                        </button>
                      </div>
                    ) : (
                      <span className="gis-week-sent-badge pending">Pendente</span>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Footer */}
            <div className="gis-footer-bar">
              <div className="gis-hint">
                <Icon name="shield" size={13}/>
                <span>Dados criptografados AES-GCM 256-bit. Desbloqueios ficam registrados com data e responsável.</span>
              </div>
              <div className="gis-month-total">
                <div className="mt-label">Total {MONTH_NAMES[viewMonth]}</div>
                <div className="mt-value">
                  {(() => { let t = 0; let any = false;
                    Object.keys(vendas).forEach(k => {
                      const d = new Date(k);
                      if (d.getFullYear()===viewYear && d.getMonth()===viewMonth && vendas[k]?.value !== null) { t += vendas[k].value||0; any=true; }
                    });
                    return any ? fmtBRL(t) : '—';
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* Unlock log */}
          {Object.keys(diasLiberados).length > 0 && (
            <div className="card" style={{ marginTop:20 }}>
              <div className="card-head"><h3>🔓 Histórico de Desbloqueios</h3></div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {Object.entries(diasLiberados)
                  .sort(([a],[b]) => a.localeCompare(b))
                  .map(([data, v]) => (
                    <div key={data} style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 12px', background:'var(--surface-2)', borderRadius:8, fontSize:12 }}>
                      <Icon name="unlock" size={14} style={{ color:'var(--warn)', flexShrink:0 }}/>
                      <div style={{ flex:1 }}>
                        <strong>Dia {data}</strong>
                        {v.por && <span style={{ color:'var(--ink-3)' }}> · liberado por {v.por}</span>}
                      </div>
                      <span style={{ color:'var(--ink-4)', fontSize:11 }}>aguardando novo lançamento</span>
                    </div>
                  ))
                }
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}


export function GISScreen({ user, onChange }) {
  if (!hasPerm(user, 'gis', 'view')) return <Blocked/>;

  const today = new Date();
  const [viewYear,  setViewYear]  = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  /* Vendas e semanas fechadas agora vêm do servidor, sempre da LOJA — não do
     usuário.

     O protótipo guardava tudo cifrado no localStorage, com a chave AES gerada
     pelo próprio navegador e salva ao lado do texto cifrado. Dois efeitos: a
     cifra não protegia nada (quem lê o dado lê a chave), e o registro era por
     USUÁRIO — dois funcionários da mesma loja mantinham dois conjuntos
     paralelos de faturamento, cada um vendo só o próprio. */
  const [vendas,    setVendas]    = useState({});   // { 'YYYY-MM-DD': { valor, por, em } }
  const [sentWeeks, setSentWeeks] = useState({});   // { semanaIdx: { por, em } }
  const [cryptoReady, setCryptoReady] = useState(false);   // mantido: a tela usa como "pronto p/ mostrar"
  const [editValues, setEditValues] = useState({});
  const [saved, setSaved] = useState({});
  const [erroGis, setErroGis] = useState('');
  const [infoLoja, setInfoLoja] = useState(null);
  const [diasLiberados, setDiasLiberados] = useState({});

  const anoMesAtual = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`;

  const carregarMes = async () => {
    setErroGis('');
    try {
      const d = await API.gis.mes(anoMesAtual);
      const porDia = {};
      for (const [data, info] of Object.entries(d.dias || {})) {
        porDia[data] = { value: info.valor, por: info.por, em: info.em };
      }
      setVendas(porDia);
      setSentWeeks(d.semanasFechadas || {});
      setDiasLiberados(d.diasLiberados || {});
      setInfoLoja({ id: d.lojaId, nome: d.lojaNome, podeReabrir: d.podeReabrir });
    } catch (e) {
      setVendas({}); setSentWeeks({}); setDiasLiberados({});
      setErroGis(e.message || 'Não foi possível carregar os lançamentos.');
    }
    setCryptoReady(true);
  };

  /* Recarrega ao trocar de mês: cada mês é uma consulta própria, em vez de
     puxar o histórico inteiro para a memória do navegador. */
  useEffect(() => { setCryptoReady(false); carregarMes(); }, [viewYear, viewMonth]);

  /* `saveToStorage` foi removido junto com a cifra no navegador. Cada dia é
     gravado sozinho, no servidor, por `handleBlur` — o que também evita
     reescrever o mês inteiro a cada tecla. */

  const weeks = buildCalendar(viewYear, viewMonth);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y-1); setViewMonth(11); }
    else setViewMonth(m => m-1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y+1); setViewMonth(0); }
    else setViewMonth(m => m+1);
  };

  const isWeekend = (dayOfWeek) => dayOfWeek === 0 || dayOfWeek === 6;
  const isPast = (d) => {
    const dc = new Date(viewYear, viewMonth, d); dc.setHours(0,0,0,0);
    const td = new Date(); td.setHours(0,0,0,0);
    return dc <= td;
  };
  const isToday = (d) => {
    const dc = new Date(viewYear, viewMonth, d);
    return dc.toDateString() === today.toDateString();
  };

  const handleInput = (dayKey, val) => {
    // Allow only digits, comma and dot
    const clean = val.replace(/[^0-9.,]/g, '');
    setEditValues(prev => ({ ...prev, [dayKey]: clean }));
  };

  /* Grava UM dia, no servidor. Se falhar, o valor antigo volta à tela — não
     adianta mostrar salvo o que não foi gravado.

     `dayKey` já é 'YYYY-MM-DD', o mesmo formato da coluna `data` no D1. */
  const handleBlur = async (dayKey, rawVal) => {
    const n = parseBRL(rawVal);
    const anterior = vendas[dayKey];
    setEditValues(prev => { const c = { ...prev }; delete c[dayKey]; return c; });

    /* Nada mudou? não chama o servidor. Sair do campo sem digitar é o caso
       mais comum de todos. */
    const valorAtual = anterior && anterior.value != null ? anterior.value : null;
    if (n === valorAtual) return;

    setVendas(prev => ({ ...prev, [dayKey]: { ...(prev[dayKey] || {}), value: n } }));
    try {
      await API.gis.salvarDia(dayKey, n);
      setSaved(prev => ({ ...prev, [dayKey]: true }));
      setTimeout(() => setSaved(prev => ({ ...prev, [dayKey]: false })), 1200);
      setErroGis('');
    } catch (e) {
      setVendas(prev => ({ ...prev, [dayKey]: anterior || { value: null } }));
      setErroGis(e.message || 'Não foi possível salvar este lançamento.');
    }
  };

  const getVal = (dayKey) => {
    if (dayKey in editValues) return editValues[dayKey];
    const stored = vendas[dayKey];
    if (!stored || stored.value === null || stored.value === undefined) return '';
    return stored.value.toLocaleString('pt-BR', { minimumFractionDigits:2, maximumFractionDigits:2 });
  };

  const getNumVal = (dayKey) => {
    const stored = vendas[dayKey];
    return (stored && stored.value !== null && stored.value !== undefined) ? stored.value : null;
  };

  /* Trava o campo quando a semana daquele dia já foi enviada — a mesma regra
     que o servidor aplica. Aqui é só para o campo não aceitar digitação que
     seria recusada com 409 no blur. */
  const diaTravado = (cell, weekIdx) => isSent(weekIdx) && !infoLoja?.podeReabrir;

  const weekTotal = (week) => {
    let total = 0; let hasAny = false;
    week.forEach((cell, di) => {
      if (!cell.outside && cell.month === viewMonth) {
        const dk = getDayKey(viewYear, viewMonth, cell.day);
        const v = getNumVal(dk);
        if (v !== null) { total += v; hasAny = true; }
      }
    });
    return hasAny ? total : null;
  };

  const monthTotal = () => {
    let total = 0; let hasAny = false;
    weeks.forEach(week => {
      const wt = weekTotal(week);
      if (wt !== null) { total += wt; hasAny = true; }
    });
    return hasAny ? total : null;
  };

  /* Enviar a semana é uma DECLARAÇÃO: depois disso o lojista não altera mais
     aqueles dias. Quem reabre é a administração, e a reabertura fica em
     `auditoria`. */
  const sendWeek = async (weekIdx, week) => {
    try {
      await API.gis.fecharSemana(anoMesAtual, weekIdx);
      await carregarMes();
    } catch (e) {
      setErroGis(e.message || 'Não foi possível enviar a semana.');
    }
  };

  const reabrirSemana = async (weekIdx) => {
    if (!confirm('Reabrir esta semana para edição? A reabertura fica registrada na auditoria.')) return;
    try {
      await API.gis.reabrirSemana(anoMesAtual, weekIdx, infoLoja?.id);
      await carregarMes();
    } catch (e) {
      setErroGis(e.message || 'Não foi possível reabrir a semana.');
    }
  };

  /* O índice da semana vem do próprio servidor agora (chave numérica), em vez
     da string `gis_ANO_MES_wN` que só existia no navegador. */
  const isSent = (weekIdx) => !!sentWeeks[weekIdx];
  const sentInfoDe = (weekIdx) => sentWeeks[weekIdx] || null;

  const weekHasData = (week) => {
    return week.some((cell, di) => {
      if (cell.outside || cell.month !== viewMonth) return false;
      return getNumVal(getDayKey(viewYear, viewMonth, cell.day)) !== null;
    });
  };

  // Alert: check if any past week has not been sent
  const overdueWeeks = weeks.filter((week, wi) => {
    const lastCellInWeek = week.filter(c => !c.outside && c.month === viewMonth).pop();
    if (!lastCellInWeek) return false;
    const lastDate = new Date(viewYear, viewMonth, lastCellInWeek.day);
    lastDate.setHours(23,59,59,999);
    return lastDate < today && !isSent(wi) && weekHasData(week);
  });

  const MONTH_NAMES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
  const DAY_NAMES   = ['DOM','SEG','TER','QUA','QUI','SEX','SÁB'];

  const mt = monthTotal();
  // stats
  const diasPreenchidos = Object.keys(vendas).filter(k => {
    const d = new Date(k); return d.getFullYear()===viewYear && d.getMonth()===viewMonth && vendas[k]?.value !== null;
  }).length;
  const diasDoMes = new Date(viewYear, viewMonth+1, 0).getDate();
  const semanaEnviadas = Object.keys(sentWeeks).filter(k => k.startsWith(`gis_${viewYear}_${String(viewMonth+1).padStart(2,'0')}`)).length;

  return (
    <div className="gis-shell">
      {!cryptoReady && (
        <div style={{ textAlign:'center', padding:60, color:'var(--ink-3)' }}>
          <Icon name="shield" size={28} style={{ display:'block', margin:'0 auto 12px', color:'var(--navy)' }}/>
          <div style={{ fontSize:13 }}>Descriptografando dados…</div>
        </div>
      )}
      {cryptoReady && (<>
      {/* Header */}
      <div className="gis-topbar">
        <div className="page-title">
          <h2 style={{ display:'flex', alignItems:'center', gap:10 }}>
            <Icon name="chart-bar" size={22} style={{ color:'var(--navy)' }}/>
            GIS · Declaração de Vendas Diárias
          </h2>
          <p style={{ marginTop:2 }}>{user.store || user.nome} · {MONTH_NAMES[viewMonth]} de {viewYear}</p>
        </div>
        <div className="gis-nav-month">
          <span className="gis-month-adj" onClick={prevMonth}>‹ {MONTH_NAMES[viewMonth===0?11:viewMonth-1].slice(0,3)}</span>
          <div style={{ textAlign:'center' }}>
            <div className="gis-month-label">{MONTH_NAMES[viewMonth]} / {viewYear}</div>
          </div>
          <span className="gis-month-adj" onClick={nextMonth}>{MONTH_NAMES[viewMonth===11?0:viewMonth+1].slice(0,3)} ›</span>
        </div>
      </div>

      {/* Stat cards */}
      <div className="gis-status-panel">
        <div className="gis-stat-card">
          <div className="gis-stat-icon" style={{ background:'var(--info-bg)' }}><Icon name="calendar" size={18} style={{ color:'var(--info)' }}/></div>
          <div>
            <div className="gis-stat-num" style={{ color:'var(--info)' }}>{diasPreenchidos}/{diasDoMes}</div>
            <div className="gis-stat-lbl">Dias do mês preenchidos</div>
          </div>
        </div>
        <div className="gis-stat-card">
          <div className="gis-stat-icon" style={{ background:'var(--ok-bg)' }}><Icon name="send" size={18} style={{ color:'var(--ok)' }}/></div>
          <div>
            <div className="gis-stat-num" style={{ color:'var(--ok)' }}>{semanaEnviadas}</div>
            <div className="gis-stat-lbl">Semanas enviadas</div>
          </div>
        </div>
        <div className="gis-stat-card">
          <div className="gis-stat-icon" style={{ background:'var(--gold-soft)' }}><Icon name="chart-bar" size={18} style={{ color:'var(--gold-2)' }}/></div>
          <div>
            <div className="gis-stat-num" style={{ color:'var(--gold-2)', fontSize:16 }}>{mt !== null ? fmtBRL(mt) : '—'}</div>
            <div className="gis-stat-lbl">Total do mês</div>
          </div>
        </div>
      </div>

      {/* Alert banners */}
      {overdueWeeks.length > 0 && (
        <div className="gis-alert-banner">
          ⚠️ <span>Enviar semana atual até: <strong>IMEDIATO</strong>, o envio desta semana está atrasado. Clique em <strong>Enviar semana</strong> nas linhas pendentes.</span>
        </div>
      )}

      {/* Calendar */}
      <div className="gis-calendar">
        {/* Header row */}
        <div className="gis-cal-head">
          {DAY_NAMES.map(d => (
            <div key={d} className={`gis-cal-head-cell${d==='DOM'||d==='SÁB'?' total-col':''}`}>{d}</div>
          ))}
          <div className="gis-cal-head-cell total-col">Total da semana</div>
        </div>

        {/* Week rows */}
        {weeks.map((week, wi) => {
          const wTotal = weekTotal(week);
          const sent = isSent(wi);
          const hasData = weekHasData(week);
          const sentInfo = sentInfoDe(wi);
          // Is this week in the past?
          const lastCell = week.filter(c => !c.outside && c.month === viewMonth).pop();
          const weekPast = lastCell ? new Date(viewYear, viewMonth, lastCell.day) < today : false;

          return (
            <div key={wi} className="gis-week-row">
              {week.map((cell, di) => {
                const isWE = isWeekend(di);
                const outside = cell.outside;
                const past = !outside && isPast(cell.day) && cell.month === viewMonth;
                const dayKey = !outside ? getDayKey(viewYear, cell.month, cell.day) : null;
                const filled = dayKey && getNumVal(dayKey) !== null;
                const todayCell = !outside && isToday(cell.day) && cell.month === viewMonth;
                // A day is editable if: week not sent, OR admin unlocked this specific day
                /* A liberação de dia agora é uma linha no D1 (`gis_dias_liberados`),
                   e não mais uma chave falsa dentro do mapa de semanas. Quem
                   pode reabrir (administração) edita sempre. */
                const dayUnlocked = dayKey ? (!!diasLiberados[dayKey] || !!infoLoja?.podeReabrir) : false;
                const inputDisabled = sent && !dayUnlocked;

                let cellClass = 'gis-day-cell';
                if (isWE)    cellClass += ' weekend';
                if (outside) cellClass += ' outside';
                if (todayCell) cellClass += ' today';
                if (!outside && !isWE && past) cellClass += ' past';
                if (!outside && !isWE && !past && !outside && cell.month === viewMonth) cellClass += ' future';
                if (filled)  cellClass += ' has-value';

                return (
                  <div key={di} className={cellClass}>
                    {!outside && (
                      <>
                        <div className="gis-day-num" style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                          <span>{!outside && cell.day}</span>
                          {dayUnlocked && <span title="Dia desbloqueado pelo administrador" style={{ fontSize:10 }}>🔓</span>}
                        </div>
                        {cell.month === viewMonth && (
                          <input
                            type="text"
                            className={`gis-day-input${filled ? ' filled' : ''}`}
                            value={getVal(dayKey)}
                            onChange={e => handleInput(dayKey, e.target.value)}
                            onBlur={e => handleBlur(dayKey, e.target.value)}
                            onKeyDown={e => {
                              if(e.key==='Enter') { e.target.blur(); return; }
                              // Allow: backspace, delete, tab, arrows, home, end
                              const allow = ['Backspace','Delete','Tab','ArrowLeft','ArrowRight','Home','End'];
                              if (allow.includes(e.key)) return;
                              // Allow: digits, comma, dot
                              if (/^[0-9.,]$/.test(e.key)) return;
                              e.preventDefault();
                            }}
                            inputMode="decimal"                            placeholder="0,00"
                            disabled={inputDisabled}
                            title={inputDisabled ? 'Semana já enviada. Solicite desbloqueio ao administrador.' : filled ? `R$ ${getNumVal(dayKey)?.toLocaleString('pt-BR',{minimumFractionDigits:2})}` : 'Clique para inserir o valor'}
                          />
                        )}
                      </>
                    )}
                  </div>
                );
              })}

              {/* Total + Enviar */}
              <div className="gis-day-cell gis-total-cell">
                <div className="total-label">Semana {wi+1}</div>
                {wTotal !== null ? (
                  <div className="total-value has-value">{fmtBRL(wTotal)}</div>
                ) : (
                  <div className="total-value empty">—</div>
                )}
                {sent ? (
                  <span className="gis-week-sent-badge">
                    <Icon name="check" size={10}/> Enviado{sentInfo?.em ? ' ' + new Date(sentInfo.em).toLocaleDateString('pt-BR',{day:'2-digit',month:'2-digit'}) : ''}
                    {sentInfo?.por ? <span style={{ opacity:.75 }}> · {sentInfo.por}</span> : null}
                  </span>
                ) : hasData && hasPerm(user,'gis','create') ? (
                  <div className="gis-week-action">
                    <button
                      className={`btn btn-sm ${weekPast ? 'btn-danger' : 'btn-primary'}`}
                      onClick={() => sendWeek(wi, week)}
                      title="Marcar esta semana como enviada"
                    >
                      <Icon name="send" size={11}/>
                      {weekPast ? 'Enviar (atrasado)' : 'Enviar semana'}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}

        {/* Footer */}
        <div className="gis-footer-bar">
          <div className="gis-hint">
            <Icon name="lightbulb" size={13}/>
            <span>Se houver centavos, utilize ponto ou vírgula na digitação do valor.</span>
          </div>
          <div className="gis-month-total">
            <div className="mt-label">Total {MONTH_NAMES[viewMonth]}</div>
            <div className="mt-value">{mt !== null ? fmtBRL(mt) : '—'}</div>
          </div>
        </div>
      </div>

      {/* Admin view: all stores summary */}
      {hasPerm(user,'gis-admin','view') && (
        <GISAdminPanel viewYear={viewYear} viewMonth={viewMonth} MONTH_NAMES={MONTH_NAMES}/>
      )}
      </>)}
    </div>
  );
}


/* ── Consolidado — total por loja no mês ──────────────────────────
   O somatório passou a ser feito pelo servidor, em SQL, sobre centavos
   inteiros. Antes o navegador baixava o registro cifrado de TODAS as lojas,
   decifrava um a um e somava em ponto flutuante.

   Três problemas naquilo: o total do shopping saía com centavos de diferença
   (float); a decifragem só funcionava no navegador que criou cada registro,
   então na prática a tabela vinha cheia de linhas vazias para quem não fosse
   o autor; e o dado de todas as lojas trafegava para qualquer pessoa que
   abrisse a tela, com o filtro sendo só visual. */
export function GISAdminPanel({ viewYear, viewMonth, MONTH_NAMES }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [totalGeral, setTotalGeral] = useState(0);

  useEffect(() => {
    let vivo = true;
    setLoading(true); setErro('');
    const anoMes = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`;
    API.gis.consolidado(anoMes)
      .then(d => {
        if (!vivo) return;
        setRows((d.lojas || []).map(l => ({
          store: l.nome, periodo: anoMes, total: l.total,
          dias: l.diasLancados, semanas: l.semanasEnviadas,
        })));
        setTotalGeral(d.total || 0);
      })
      .catch(e => { if (vivo) setErro(e.message || 'Não foi possível carregar o consolidado.'); })
      .finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, [viewYear, viewMonth]);

  return (
    <div className="card" style={{ marginTop:24 }}>
      <div className="card-head">
        <h3>📊 Resumo Geral — Todas as Lojas ({MONTH_NAMES[viewMonth]}/{viewYear})</h3>
        {/* O selo antigo dizia "Dados criptografados AES-GCM 256-bit". Era
            verdade tecnicamente e falso na prática: a chave ficava no
            localStorage, ao lado do texto cifrado. Trocado por algo que
            descreve a proteção que existe de fato. */}
        <span className="status-pill ok"><span className="ind"/>Somado no servidor · acesso restrito por permissão</span>
      </div>
      {erro && (
        <div style={{ margin:'0 0 12px',padding:'10px 14px',background:'var(--err-bg)',border:'1px solid #f5c2c2',borderRadius:8,color:'var(--err)',fontSize:13 }}>
          {erro}
        </div>
      )}
      {loading ? (
        <div style={{ textAlign:'center', padding:24, color:'var(--ink-3)', fontSize:13 }}>
          <Icon name="chart-bar" size={16} style={{ verticalAlign:'middle', marginRight:6 }}/>Carregando consolidado…
        </div>
      ) : (
        <div style={{ overflowX:'auto' }}>
          <table className="users-table">
            <thead>
              <tr><th>Loja / Responsável</th><th>Período</th><th>Dias preenchidos</th><th>Semanas enviadas</th><th>Total do mês</th><th>Última atualização</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} style={{ textAlign:'center', padding:24, color:'var(--ink-3)' }}>Nenhum registro encontrado.</td></tr>}
              {rows.map((r, i) => (
                <tr key={i}>
                  <td><div style={{ fontWeight:600 }}>{r.store || '—'}</div></td>
                  <td><div style={{ fontSize:12, color:'var(--ink-3)' }}>{r.periodo || '—'}</div></td>
                  <td>{r.dias}</td>
                  <td>{r.semanas}</td>
                  <td><strong style={{ color:'var(--ok)' }}>{r.total > 0 ? fmtBRL(r.total) : '—'}</strong></td>
                  <td style={{ fontSize:11, color:'var(--ink-3)' }}>{r.updatedAt ? new Date(r.updatedAt).toLocaleString('pt-BR') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
